/** District categories share the Politicians index's server-filtered tabs and
 * pagination. The optional state selector composes with kind and global cycle. */
import { EntityIndex } from './base';
import { canonicalPath } from '@electinfo/common';
import { US_STATE_OPTIONS } from './us-states';
import { sldDisplayName, type SLDKind } from './district-labels';
import type { EntityConfig, EntitySearchResult } from './types';

// Census delegate districts in these territories are present in the graph
// even when published slug aliases lag their current identities (sites#462).
const DISTRICT_STATE_OPTIONS: Array<[string, string]> = [
  ...US_STATE_OPTIONS,
  ['AS', 'American Samoa'], ['GU', 'Guam'], ['MP', 'Northern Mariana Islands'], ['VI', 'U.S. Virgin Islands'],
];
DISTRICT_STATE_OPTIONS.sort((a, b) => a[1].localeCompare(b[1]));

const TABS = [
  { id: 'all', label: 'All', filter: null },
  { id: 'congressional', label: 'Congressional', filter: 'district_type:cd' },
  { id: 'state-senate', label: 'State Senate', filter: 'district_type:sldu' },
  { id: 'state-house', label: 'State House', filter: 'district_type:sldl' },
];

function formatDistrict(result: EntitySearchResult) {
  let metadata: { district_type?: string; district_slug?: string } = {};
  try { metadata = JSON.parse(result.metadata || '{}') || {}; } catch { /* neutral fallback */ }
  const kind = metadata.district_type ?? 'cd';
  const state = result.state || '';
  const knownState = DISTRICT_STATE_OPTIONS.find(([code]) => code === state);
  const stateName = knownState?.[1] || state;
  const number = String(result.district ?? '');
  const slug = metadata.district_slug || '';
  const chamber = kind === 'sldu' ? 'sd' : kind === 'sldl' ? 'hd' : null;
  const validSLD = knownState && chamber && /^[0-9A-Z][0-9A-Z-]*$/.test(number) && number !== 'ZZZ' &&
    slug === `${state}-${chamber}-${number}`.toLowerCase();
  const cdSlug = slug || `${state}-${number.padStart(2, '0')}`;
  const validCD = kind === 'cd' && /^[A-Z]{2}$/.test(state) && /^[A-Z]{2}-[0-9A-Z]{2}$/i.test(cdSlug) &&
    cdSlug.toLowerCase() === `${state}-${number.padStart(2, '0')}`.toLowerCase();
  // 98 is Census's at-large delegate code. Its slug can be explicitly
  // retired while the live graph UUID page renders. Do not manufacture a
  // replacement -00 alias: those can point to a different, obsolete UUID.
  const delegateStoragePath = kind === 'cd' && number === '98';
  // The API falls back to the slug or id when a District has no name.
  const tigerName = result.name && result.name !== slug && result.name !== result.id ? result.name : null;
  const category = validSLD ? (chamber === 'sd' ? 'State Senate' : 'State House') : validCD ? 'Congressional' : '';
  return {
    url: !delegateStoragePath && (validSLD || validCD) ? `/districts/${(validSLD ? slug : cdSlug).toLowerCase()}/` : `/${canonicalPath('District', encodeURIComponent(result.id))}/`,
    name: delegateStoragePath ? `${stateName} At-Large Delegate District` : validSLD ? sldDisplayName(stateName, kind as SLDKind, number, tigerName) : validCD ? `${cdSlug.toUpperCase()} · Congressional` : result.name || 'District',
    meta: delegateStoragePath ? 'Congressional · Delegate' : validSLD ? category : validCD ? stateName : 'District',
  };
}

class DistrictIndex extends EntityIndex {
  // No initializers: EntityIndex may call setup synchronously from super().
  private stateCode?: string;

  getConfig(): EntityConfig {
    return {
      entityType: 'District',
      placeholder: 'Search districts by name...',
      browseTitle: 'All Districts',
      sortField: 'district_slug',
      sortOrder: 'ASC',
      tabs: TABS,
      formatResult: formatDistrict,
    };
  }

  protected insertSearchUI(): void {
    super.insertSearchUI();
    const container = document.querySelector<HTMLElement>('.entity-search-container');
    const list = document.getElementById('list-view');
    if (container && list) list.appendChild(container);
    const selector = document.createElement('div');
    selector.className = 'entity-filter-dropdown';
    selector.innerHTML = `<label for="district-state-select">State:</label>
      <select id="district-state-select"><option value="">All states</option>
      ${DISTRICT_STATE_OPTIONS.map(([code, name]) => `<option value="${code}">${name}</option>`).join('')}</select>`;
    document.getElementById('entity-filter-tabs')?.after(selector);
    selector.querySelector('select')?.addEventListener('change', e => {
      this.stateCode = (e.target as HTMLSelectElement).value;
      this.loadEntities({ query: this.currentQuery, page: 1 });
    });
  }

  private syncCategoryURL(): void {
    const params = new URLSearchParams(location.search);
    const tab = TABS.find(t => t.id === params.get('tab')) || TABS[0];
    this.currentFilter = tab.filter;
    this.stateCode = DISTRICT_STATE_OPTIONS.some(([code]) => code === params.get('state')) ? params.get('state')! : '';
    const select = document.getElementById('district-state-select') as HTMLSelectElement | null;
    if (select) select.value = this.stateCode;
    document.querySelectorAll<HTMLElement>('.entity-filter-tab').forEach(button => {
      button.classList.toggle('active', button.dataset.tabId === tab.id);
      button.setAttribute('aria-pressed', String(button.dataset.tabId === tab.id));
    });
  }

  protected loadFromURL(): void {
    this.syncCategoryURL();
    super.loadFromURL();
  }

  protected handlePopState(event: PopStateEvent): void {
    this.syncCategoryURL();
    super.handlePopState(event);
  }

  protected getEffectiveFilter(): string | null {
    return [super.getEffectiveFilter(), this.stateCode ? `state:${this.stateCode}` : null].filter(Boolean).join(',') || null;
  }

  protected updateURL(query: string, page: number): void {
    // One history entry preserves category/state on back and forward.
    const url = new URL(location.href);
    const tab = TABS.find(t => t.filter === this.currentFilter) || TABS[0];
    if (tab.id !== 'all') url.searchParams.set('tab', tab.id);
    else url.searchParams.delete('tab');
    if (this.stateCode) url.searchParams.set('state', this.stateCode);
    else url.searchParams.delete('state');
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    if (page > 1) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    document.querySelectorAll<HTMLElement>('.entity-filter-tab').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.tabId === tab.id));
    });
    history.pushState({ query, page }, '', url);
  }
}

new DistrictIndex();
